import React from "react";
import {
  useGetBankAccountQuery,
  useUpdateBankAccountMutation,
} from "@/queries/hooks/bankAccountHooks";
import Input from "@/components/input";
import Button from "@/components/button";
import { Combobox } from "@/components/combobox";
import { ToggleGroup, ToggleGroupItem } from "@/components/toggle-group";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function PaymentsPanel() {
  const { t } = useTranslation("settings");
  const [isEditing, setIsEditing] = React.useState(false);
  const bankQuery = useGetBankAccountQuery();
  const updateBankAccountMutation = useUpdateBankAccountMutation();
  const bankDetails = bankQuery.data?.bank_details;
  const hasBank = bankDetails && bankDetails.accountNumber;

  const BankDetailsSchema = React.useMemo(
    () =>
      z
        .object({
          account_holder_type: z.union([
            z.literal("myself"),
            z.literal("company"),
          ]),
          bank_account_name: z
            .string()
            .min(1, t("payments.errors.enterBankAccountName")),
          account_type: z.union([
            z.literal("checking"),
            z.literal("savings"),
          ]),
          routing_number: z
            .string()
            .min(1, t("payments.errors.enterRoutingNumber")),
          account_number: z
            .string()
            .min(1, t("payments.errors.enterAccountNumber")),
          confirm_account_number: z
            .string()
            .min(1, t("payments.errors.confirmAccountNumber")),
        })
        .refine(
          (data) => data.account_number === data.confirm_account_number,
          {
            message: t("payments.errors.accountNumbersDoNotMatch"),
            path: ["confirm_account_number"],
          },
        ),
    [t],
  );

  const form = useForm<z.infer<typeof BankDetailsSchema>>({
    resolver: zodResolver(BankDetailsSchema),
    defaultValues: {
      account_holder_type: "myself",
      bank_account_name: "",
      account_type: "checking",
      routing_number: "",
      account_number: "",
      confirm_account_number: "",
    },
    mode: "onBlur",
  });

  React.useEffect(() => {
    if (!bankDetails) return;
    form.reset({
      account_holder_type:
        bankDetails.accountHolderType === "company" ? "company" : "myself",
      bank_account_name: bankDetails.bankAccountName || "",
      account_type:
        bankDetails.accountType === "savings" ? "savings" : "checking",
      routing_number: bankDetails.routingNumber || "",
      account_number: bankDetails.accountNumber || "",
      confirm_account_number: bankDetails.accountNumber || "",
    });
  }, [bankDetails, form]);

  const onSubmit = async (values: z.infer<typeof BankDetailsSchema>) => {
    const apiPayload = {
      id: bankDetails?.id || null,
      userId: null,
      accountHolderType: values.account_holder_type,
      bankAccountName: values.bank_account_name,
      accountType: values.account_type,
      routingNumber: values.routing_number,
      accountNumber: values.account_number,
      isActive: true,
      createdAt: null,
      updatedAt: null,
    };
    await updateBankAccountMutation.mutateAsync(apiPayload);
    bankQuery.refetch();
    if (hasBank) setIsEditing(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-xl font-semibold text-garnet mb-2">
        {t("payments.title")}
      </h3>
      {hasBank && !isEditing ? (
        <>
          <table className="w-full border rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-garnet">
                  {t("payments.table.type")}
                </th>
                <th className="text-left px-4 py-2 font-medium text-garnet">
                  {t("payments.table.bankName")}
                </th>
                <th className="text-left px-4 py-2 font-medium text-garnet">
                  {t("payments.table.last4")}
                </th>
                <th className="text-left px-4 py-2 font-medium text-garnet">
                  {t("payments.table.status")}
                </th>
                <th className="text-left px-4 py-2 font-medium text-garnet">
                  {t("payments.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t">
                <td className="px-4 py-2">{t("payments.table.bankAccount")}</td>
                <td className="px-4 py-2">
                  {bankDetails.bankAccountName || "-"}
                </td>
                <td className="px-4 py-2">
                  •••• {bankDetails.accountNumber?.slice(-4) || "----"}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700`}
                  >
                    {t("payments.table.verified")}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    {t("actions.edit", { ns: "common" })}
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      ) : (
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <Combobox
            label={t("payments.whoseAccount")}
            options={[
              { label: t("payments.holder.myself"), value: "myself" },
              { label: t("payments.holder.company"), value: "company" },
            ]}
            value={form.watch("account_holder_type")}
            onChange={(next: string | null) => {
              form.setValue(
                "account_holder_type",
                (next as any) === "company" ? "company" : "myself",
                { shouldDirty: true },
              );
            }}
          />
          <ToggleGroup
            type="single"
            value={form.watch("account_type")}
            onValueChange={(value) =>
              form.setValue(
                "account_type",
                (value as any) === "savings" ? "savings" : "checking",
                { shouldDirty: true },
              )
            }
            className="mb-2"
          >
            <ToggleGroupItem value="checking">
              <span>{t("payments.type.checking")}</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="savings">
              <span>{t("payments.type.savings")}</span>
            </ToggleGroupItem>
          </ToggleGroup>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            <Input
              label={t("payments.bankAccountName")}
              {...form.register("bank_account_name")}
              state={
                form.formState.errors.bank_account_name ? "error" : "default"
              }
              hint={form.formState.errors.bank_account_name?.message}
            />
            <Input
              label={t("payments.routingNumber")}
              {...form.register("routing_number")}
              state={form.formState.errors.routing_number ? "error" : "default"}
              hint={form.formState.errors.routing_number?.message}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            <Input
              label={t("payments.accountNumber")}
              {...form.register("account_number")}
              state={form.formState.errors.account_number ? "error" : "default"}
              hint={form.formState.errors.account_number?.message}
            />
            <Input
              label={t("payments.confirmAccountNumber")}
              {...form.register("confirm_account_number")}
              state={
                form.formState.errors.confirm_account_number
                  ? "error"
                  : "default"
              }
              hint={
                form.formState.errors.confirm_account_number?.message
              }
            />
          </div>
          <div className="flex gap-4">
            <Button
              type="submit"
              variant="primary"
              disabled={updateBankAccountMutation.isPending}
            >
              {hasBank
                ? t("payments.updateBank")
                : t("payments.addBank")}
            </Button>
            {isEditing && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                {t("actions.cancel", { ns: "common" })}
              </Button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
